import React, { useState } from "react";
import { Grid, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { style } from "./style";

import {
  IAllOrderTypeEnums,
  IOrderStatus,
  ITestBookingDetail,
} from "@/models/ApiRequestResponse/patient";
import { IButtonVariant } from "@/models/button";
import { IOrderStatusEnum } from "@/models/ApiRequestResponse/pharmacy";
import { IAllOrderListProps } from "@/models/patient";
import { ISessionKeys } from "@/models";

import GenericCardHeader from "@/components/GenericCardHeader";
import GetImages from "@/components/GetImages";
import Button from "@/components/Button";
import Confirmation from "@/components/Confirmation";
import TruncatedText from "@/components/TruncatedText";

import {
  allOrders,
  fetchAllOrdersList,
  fetchHomeModules,
  setAllOrders,
  setAllOrderStatusUpdate,
} from "@/store/slices/patient/home";
import { handleIsInfiniteScrollFilterChanges } from "@/store/slices/config";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { openClinicMapLocation } from "@/utils";
import { INotifyEnum, notify } from "@/utils/toaster";
import { storage } from "@/utils/Storage";
import { updateDiagnosticOrderApi } from "@/utils/apis/diagnostic";

interface ITestBookingCardProps extends ITestBookingDetail {
  filters?: IAllOrderListProps;
}

const TestBookingCard: React.FC<ITestBookingCardProps> = (props) => {
  const {
    id,
    status,
    name,
    date,
    address,
    city,
    pincode,
    tests,
    phone_number,
    latitude,
    longitude,
    filters,
    gender,
    age,
    booking_timestamp,
    order_no,
    patientName,
  } = props;
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const allOrdersList = useAppSelector(allOrders);

  const navigate = useNavigate();

  const userId = storage.getSessionToken(ISessionKeys.USER_ID);

  const cancelOrderModalHandler = (): void =>
    setIsCancelModalOpen(!isCancelModalOpen);

  const getDirectionHandler = (): void => {
    openClinicMapLocation(latitude ?? 0, longitude ?? 0);
  };

  const testBookingOrderCancelHandler: () => Promise<void> = async () => {
    const cancelOrderResponse = await updateDiagnosticOrderApi({
      id,
      status: IOrderStatusEnum.CANCELLED,
    });
    if ("result" in cancelOrderResponse) {
      notify(INotifyEnum.SUCCESS, cancelOrderResponse?.message);
      cancelOrderModalHandler();
      if (filters) {
        dispatch(setAllOrders([]));
        dispatch(handleIsInfiniteScrollFilterChanges());
        dispatch(fetchAllOrdersList({ ...filters, offset: "0" }));
      } else if (allOrdersList?.length === 0) {
        dispatch(fetchHomeModules({ userId: userId ?? "" }));
      } else {
        dispatch(
          setAllOrderStatusUpdate({
            ...props,
            status: IOrderStatusEnum.CANCELLED,
          })
        );
      }
    } else {
      notify(INotifyEnum.ERROR, cancelOrderResponse?.message);
    }
  };

  const bookAgainHandler = (): void => navigate("/test-reports");

  return (
    <Grid item sx={style.cardContainer}>
      <GenericCardHeader
        orderStatus={status}
        doctorId={""}
        name={name}
        bio={` 
          ${dayjs
            .unix(booking_timestamp)
            .format("D MMMM YYYY, h:mmA")} • Order Id ${order_no}`}
        imageSvg={
          <Grid item sx={style.headerIcon}>
            <GetImages name="TestBookingIcon" height={"36"} width={"25"} />
          </Grid>
        }
        orderType={IAllOrderTypeEnums.DIAGNOSTIC_TEST}
      />

      <Grid item sx={style.footer}>
        <Typography
          component={"p"}
          sx={{
            ...style.footerText,
            ...style.patientNameSpace,
          }}
        >
          <GetImages name={"SmileOrderIcon"} width="18" height="16" />
          {`${patientName}, ${gender}, ${age}`}
        </Typography>
        <Typography
          component={"p"}
          sx={{ ...style.footerText, ...style.spacing }}
        >
          <GetImages name={"PrescriptionTestIcon"} width="18" height="16" />
          {tests?.join(", ")}
        </Typography>
        {phone_number && (
          <Typography
            component={"p"}
            sx={{ ...style.footerText, ...style.hideShowBlock }}
          >
            <GetImages name={"TelePhoneIcon"} width="18" height="16" />
            <FormattedMessage
              id="contact_support"
              defaultMessage="Contact Support"
            />{" "}
            - {phone_number}
          </Typography>
        )}
        <Grid item sx={style.clinicInfo}>
          <TruncatedText
            text={`${address}, ${city} - ${pincode}`}
            muiStyles={{ ...style.footerText, ...style.spacing }}
            maxLength={30}
            icon={<GetImages name={"ClinicSmallIcon"} width="18" height="16" />}
          />

          {(status === IOrderStatus.PENDING ||
            status === IOrderStatus.CONFIRMED) && (
            <Grid item sx={style.clinicLocation} onClick={getDirectionHandler}>
              <GetImages name="HospitalLocationIcon" width="16" height="16" />
              <Typography component={"p"} sx={style.mapText}>
                <FormattedMessage id="maps" defaultMessage="Maps" />
              </Typography>
            </Grid>
          )}
        </Grid>
        {(status === IOrderStatus.PENDING ||
          status === IOrderStatus.CONFIRMED) && (
          <Grid item sx={style.buttonContainer}>
            <Button
              variant={IButtonVariant.WHITE}
              btnTrigger={cancelOrderModalHandler}
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
            <Grid item sx={style.directionButton}>
              <Button
                variant={IButtonVariant.WHITE}
                btnTrigger={getDirectionHandler}
              >
                <GetImages
                  name="LocationAccessSmallIcon"
                  width="26"
                  height="20"
                />
                <FormattedMessage id="direction" defaultMessage="Direction" />
              </Button>
            </Grid>
            <Grid
              item
              sx={{
                ...style.contactSupportButton,
                ...style.contactSupportOrChatButton,
              }}
              onClick={() => (window.location.href = `tel:${phone_number}`)}
            >
              <GetImages name="ContactSupportIcon" width="16" height="16" />
            </Grid>
          </Grid>
        )}
        {status === IOrderStatus.CANCELLED && (
          <Grid item sx={{ marginTop: "0.8rem" }}>
            <Button
              variant={IButtonVariant.WHITE}
              btnTrigger={bookAgainHandler}
            >
              <FormattedMessage id="book_again" defaultMessage="Book Again" />
            </Button>
          </Grid>
        )}
        {status === IOrderStatus.COMPLETED && (
          <Grid
            item
            sx={{ marginTop: "0.8rem", display: { xs: "block", md: "none" } }}
          >
            <Button
              btnTrigger={() => (window.location.href = `tel:${phone_number}`)}
            >
              <GetImages name="TelePhoneWhiteIcon" width="26" height="20" />
              <FormattedMessage id="call" defaultMessage="Call" />
            </Button>
          </Grid>
        )}
      </Grid>
      {isCancelModalOpen && (
        <Confirmation
          isOpen={isCancelModalOpen}
          closeHandler={cancelOrderModalHandler}
          title={"cancel_order"}
          alterMessage="cancel_order_alert_message"
          actionHandler={testBookingOrderCancelHandler}
        />
      )}
    </Grid>
  );
};

export default TestBookingCard;
